import { Fragment } from 'react';

import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { IconFormatter } from '@components/icons/formatter';
import { IconIntegration } from '@components/icons/integration';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  MappableActionVariable,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { fieldTypeIconMap } from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';
import { isBase64File } from '@utils/strings';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { functionLabel } from './helpers';
import { FunctionsForField } from './journey-sidebar';

import {
  ActionResultCard,
  EditableActionContent,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  useEditingContext,
  TriggerMethod,
  Base64DownlodableLink,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'formatter';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconFormatter} iconColor="indigo100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('description')} text={action.description} />
      <ActionResultCard
        action={action}
        title={t('title')}
        icon={<IconIntegration />}
        errorTitle={actionErrorTitle[action.action]}
      >
        <Stack space={4}>
          {action.status === 'completed' ? (
            <CompletedContent action={action} />
          ) : (
            <IncompleteContent action={action} />
          )}
        </Stack>
      </ActionResultCard>
    </Stack>
  );
};

const CompletedContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const dataType = typeof action.data;
  const isValueObject =
    typeof action.data === 'object' &&
    Object.keys(action.data).length === 1 &&
    'value' in action.data;
  return (
    <>
      <Text font="heading2" text={t('requestData')} />
      {dataType === 'string' || dataType === 'number' ? (
        <LabelTextGroup key="value" label={t('value')} text={action.data} />
      ) : isValueObject ? (
        <LabelTextGroup key="value" label={t('value')} text={action.data.value} />
      ) : (
        Object.keys(action.data).map((key) => (
          <Fragment key={key}>
            {isBase64File(JSON.stringify(action.data[key])) ? (
              <div>
                <Text font="semibold14" text={key} />
                <Base64DownlodableLink base64string={action.data[key]} showAsCard />
              </div>
            ) : (
              <LabelTextGroup label={key} text={JSON.stringify(action.data[key])} />
            )}
          </Fragment>
        ))
      )}
    </>
  );
};

const IncompleteContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { startEditing } = useEditingContext();
  const fieldVariable = action.values?.field.variables
    ? (Object.values(action.values.field.variables)[0] as MappableActionVariable)
    : undefined;

  const fieldLabel = fieldVariable?.label;
  return (
    <Stack space={4}>
      <LabelTextGroup label={t('field')} text={fieldLabel} />
      <LabelTextGroup label={t('function')} text={functionLabel(action.values?.function)} />
      <Sidebar.Footer>
        <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
      </Sidebar.Footer>
    </Stack>
  );
};

type Data = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  function: string;
  field: string;
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { stopEditing } = useEditingContext();
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateFormStore();
  const fieldValue = formStore.useFieldValue('field') as VariableValue;
  const functionValue = formStore.useFieldValue('function') as VariableValue;
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const handleSave = async (data: Data) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: true,
      action: 'formatter',
      values: {
        function: data.function,
        field: data.field,
      },
    });
  };

  const fieldVariable = fieldValue?.variables
    ? (Object.values(fieldValue.variables)[0] as MappableActionVariable)
    : undefined;

  const fieldType = fieldVariable?.type;
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    excludedTypes: [],
  });

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('configuration')}>
          <Stack space={4}>
            <Form.FieldMappingField
              id="field"
              label={t('field')}
              variableGroups={variableGroups}
              defaultValue={action.values?.field}
              singleLine
              plaintext
              info={t('fieldInfo')}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <FunctionsForField
              key={fieldValue?.value}
              fieldType={fieldType}
              defaultValue={action.values?.function}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('formatterOutput')}>
          <Stack space={2}>
            <Text text={t('output')} font="semibold14" />
            {functionValue && fieldValue?.value ? (
              <Output fieldVariable={fieldVariable} data={action} />
            ) : (
              <Text text={t('selectToSeeOutput')} font="regular14" color="textHelper" />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const Output = ({
  fieldVariable,
  data,
}: {
  fieldVariable?: MappableActionVariable;
  data: TravellerJourneyActionDetailed;
}) => {
  // When we support more formatting options, we can make this more dynamic
  const fieldAction = fieldVariable?.action;
  const fieldField = fieldVariable?.field ?? '';
  const actionVariables = data.actionVariables?.find(
    (variable) => variable.shortId === fieldAction
  );
  const arrayTemplate = actionVariables?.fields?.[fieldField]?.template ?? [];
  return (
    <>
      {arrayTemplate.map((item) => {
        const Icon = fieldTypeIconMap[item.type ?? 'text'];
        return (
          <Stack key={item.label} space={2} orientation="horizontal" align="center">
            <Icon color="elementPurpleInvert" />
            <Text text={item.label} font="regular14" />
          </Stack>
        );
      })}
    </>
  );
};
const sidebarLocale = {
  en: {
    title: 'Formatter',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    started: 'Started',
    completed: 'Completed',
    retry: 'Retry',
    template: 'Template',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    requestData: 'Request data',
    responseData: 'Response data',
    errorResponse: 'Error response',
    addMore: 'Add more',
    formatter: 'Formatter',
    actionDetails: 'Action details',
    configuration: 'Configuration',
    formatterOutput: 'Formatter output',
    output: 'Output',
    selectToSeeOutput: 'Select a function to see output',
    function: 'Function',
    selectFunction: 'Select function',
    noFunctionsAvailable: 'No functions available for selected field',
    selectField: 'Select field for available functions',
    firstItemOfArray: 'First item of array',
    lastItemOfArray: 'Last item of array',
    randomItemOfArray: 'Random item of array',
    field: 'Field',
    fieldInfo: 'Field to format',
    value: 'Value',
  },
};
